import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { useLoadingStore } from '@/shared/model';
import { SUPPORT_URL_REQUEST_TYPE } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { t } from 'ttag';
import { CATEGORY_LIST } from '../config/newSupportRequest';

/**
 * Backs the new-support-request page: resolves the request category from the route, builds the facility options and exposes the category and select handlers. Support facilities from the store become label/value options ordered by label with the user's selected facility preselected, the document title is set, and an absent or unknown requestType param redirects back to the support route.
 *
 * @returns Page state, the loading setter and the change handlers.
 */
export const useNewSupportRequest = () => {
  const params = useParams();

  const navigate = useNavigate();

  /**
   * The value of the facility currently selected by the signed-in user. Subscribed from the user store so the new-support-request page reacts whenever the active facility changes.
   *
   * @returns Identifier of the user's selected facility.
   */
  const selectedFacilityValue = useUserStore((s) => s.selectedFacility?.value);
  /**
   * The facilities that support requests may be raised for. Subscribed from the support store so the request page rebuilds its facility options when the stored list changes.
   *
   * @returns The support facility list held in the store.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  /**
   * Page-level loading action bound for the new-support-request page and handed down to each category form. Selected from the loading store's loading-text setter, so invoking it writes the given text straight into global loading state.
   *
   * @param loadingText - Text to display in the global page loader.
   * @returns Nothing.
   */
  const setLoadingPage = useLoadingStore((s) => s.setLoadingText);

  const [state, setState] = useMergeState<any>({
    isScroll: false,
    category: CATEGORY_LIST[0].value,
    selectedFacility: undefined,
  });

  /**
   * Leaves the new-support-request page. Navigates to the support route.
   *
   * @returns Nothing.
   */
  const backToPrevious = () => {
    navigate('/support');
  };

  /**
   * Stores a select change made on the new-support-request page, keeping the whole option object. The option is merged into state under the supplied field name while the raw value is ignored.
   *
   * @param name - State field to update, matching the select name.
   * @param value - Raw value of the selected option, unused.
   * @param option - Full option object stored in state.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any, option: any) => {
    setState({ [name]: option });
  };

  /**
   * Switches the new-support-request page to another request category. Navigates to the new-support-request route carrying the chosen category so the selection is driven by the url, while the field name is ignored.
   *
   * @param name - Name of the radio group, unused.
   * @param value - Request category key appended to the route.
   * @returns Nothing.
   */
  const onChangeCategory = (name: any, value: any) => {
    navigate(`/support/new-support-request/${value}`);
  };

  useEffect(() => {
    /**
     * The support facilities reshaped into select options for the request page. Each facility contributes a label from its name and a value from its id, and the resulting options are ordered ascending by label.
     *
     * @returns Alphabetically ordered facility options.
     */
    const formattedSupportFacilities = _.orderBy(
      _.map(supportFacilities, (supportFacility) => ({
        label: supportFacility.name,
        value: supportFacility.id,
      })),
      ['label'],
      ['asc'],
    );
    /**
     * The facility option the new-support-request form should start on. Matches the sorted facility options against the user store's selected facility value and falls back to the first option of the list.
     *
     * @returns The preselected facility option.
     */
    const selectedFacility =
      _.find(formattedSupportFacilities, (x) => x.value === selectedFacilityValue) ||
      formattedSupportFacilities[0];
    setState({
      formattedSupportFacilities,
      selectedFacility,
    });
  }, [supportFacilities, selectedFacilityValue]);

  useEffect(() => {
    document.title = t`Biocare Diagnostics - New Support Request`;
  }, []);

  useEffect(() => {
    if (
      params.requestType &&
      Object.values(SUPPORT_URL_REQUEST_TYPE).includes(params.requestType)
    ) {
      setState({ category: params.requestType });
    } else {
      navigate('/support');
    }
  }, [params]);

  return {
    state,
    setLoadingPage,
    backToPrevious,
    onChange,
    onChangeCategory,
  };
};
